import { getCsrfToken } from '../utils/csrf'

const API_BASE_URL = '/api/v1'

const getFreshHeaders = async (isFormData = false) => {
  const csrfToken = await getCsrfToken()

  const headers = {
    'X-CSRF-Token': csrfToken,
    'Accept': 'application/json'
  }

  if (!isFormData) {
    headers['Content-Type'] = 'application/json'
  }

  return headers
}

const getReadHeaders = () => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  return {
    'X-CSRF-Token': csrfToken || '',
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
}

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'An error occurred' }))

    if (response.status === 422) {
      // Create an error object that contains both the general message and field errors
      const errorObj = new Error(error.error || 'Validation failed')
      errorObj.fieldErrors = error.errors
      throw errorObj
    }
    throw new Error(error.error || error.message || 'An error occurred')
  }

  // Handle empty responses (like 204 No Content)
  const contentType = response.headers.get('content-type')
  if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
    return null
  }

  return response.json()
}

const buildEventFormData = (data) => {
  const formData = new FormData()
  Object.entries(data).forEach(([key, value]) => {
    if (key === 'location') {
      formData.append('event[location_attributes][address]', value.address)
      formData.append('event[location_attributes][city]', value.city)
      formData.append('event[location_attributes][state]', value.state)
      formData.append('event[location_attributes][zip_code]', value.zip_code)
      formData.append('event[location_attributes][latitude]', value.latitude)
      formData.append('event[location_attributes][longitude]', value.longitude)
      if (value.timezone && value.timezone !== '') {
        formData.append('event[location_attributes][timezone]', value.timezone)
      }
    } else if (key === 'photo' && value instanceof File) {
      formData.append('event[photo]', value)
    } else if (key === 'turnstile_token') {
      formData.append('turnstile_token', value)
    } else {
      formData.append(`event[${key}]`, value)
    }
  })
  return formData
}

// Base API methods
const api = {
  get: (url, config = {}) => {
    return fetch(url, {
      method: 'GET',
      headers: { ...getReadHeaders(), ...config.headers },
      credentials: 'same-origin'
    }).then(handleResponse)
  },

  post: async (url, data) => {
    const formData = buildEventFormData(data)
    const headers = await getFreshHeaders(true)

    return fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'same-origin'
    }).then(handleResponse)
  },

  put: async (url, data) => {
    const formData = buildEventFormData(data)
    const headers = await getFreshHeaders(true)

    return fetch(url, {
      method: 'PUT',
      headers,
      body: formData,
      credentials: 'same-origin'
    }).then(handleResponse)
  },

  delete: async (url) => {
    const headers = await getFreshHeaders()
    return fetch(url, {
      method: 'DELETE',
      headers,
      credentials: 'same-origin'
    }).then(handleResponse)
  }
}

// API endpoints
api.events = {
  getAll: (config = {}) => {
    const params = new URLSearchParams(config.params || {})
    const url = `${API_BASE_URL}/events${params.toString() ? `?${params.toString()}` : ''}`
    return api.get(url, config)
  },
  get: (id) => {
    return api.get(`${API_BASE_URL}/events/${id}`)
  },
  getSales: (id) => {
    return api.get(`${API_BASE_URL}/events/${id}/sales`)
  },
  create: (data) => {
    return api.post(`${API_BASE_URL}/events`, data)
  },
  update: (id, data) => {
    return api.put(`${API_BASE_URL}/events/${id}`, data)
  },
  delete: (id) => {
    return api.delete(`${API_BASE_URL}/events/${id}`)
  }
}

api.sales = {
  get: (eventId, saleId) => {
    return api.get(`${API_BASE_URL}/events/${eventId}/sales/${saleId}`)
  },
  edit: (eventId, saleId) => {
    return api.get(`${API_BASE_URL}/events/${eventId}/sales/${saleId}/edit`)
  },
  update: async (eventId, saleId, formData) => {
    const headers = await getFreshHeaders(true)
    return fetch(`${API_BASE_URL}/events/${eventId}/sales/${saleId}`, {
      method: 'PUT',
      headers,
      body: formData,
      credentials: 'same-origin'
    }).then(handleResponse)
  },
  create: async (formData) => {
    const headers = await getFreshHeaders(true)
    return fetch(`${API_BASE_URL}/sales`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'same-origin'
    }).then(handleResponse)
  },
  delete: (id) => {
    return api.delete(`${API_BASE_URL}/sales/${id}`)
  },
  deleteImage: (saleId, imageId) => {
    return api.delete(`${API_BASE_URL}/sales/${saleId}/images/${imageId}`)
  },
  deletePrimaryPhoto: (saleId) => {
    return api.delete(`${API_BASE_URL}/sales/${saleId}/primary_photo`)
  },
  approve: async (id) => {
    const headers = await getFreshHeaders()
    return fetch(`${API_BASE_URL}/sales/${id}/approve`, {
      method: 'PATCH',
      headers,
      credentials: 'same-origin'
    }).then(handleResponse)
  },
  reject: async (id) => {
    const headers = await getFreshHeaders()
    return fetch(`${API_BASE_URL}/sales/${id}/reject`, {
      method: 'PATCH',
      headers,
      credentials: 'same-origin'
    }).then(handleResponse)
  }
}

api.locations = {
  getCities: () => {
    return api.get(`${API_BASE_URL}/locations/cities`)
  }
}

export { api }